import { Input } from '@/components/ui/input';
import type { QuestionnaireAnswerType, QuestionnaireQuestion } from '@shared/types/api';
import { OptionsEditor } from './options-editor';

interface QuestionnaireBuilderProps {
  questions: QuestionnaireQuestion[];
  onAddQuestion: () => void;
  onUpdateQuestion: (idx: number, patch: Partial<QuestionnaireQuestion>) => void;
  onRemoveQuestion: (idx: number) => void;
  onMoveQuestion: (idx: number, dir: -1 | 1) => void;
}

export function QuestionnaireBuilder({
  questions,
  onAddQuestion,
  onUpdateQuestion,
  onRemoveQuestion,
  onMoveQuestion,
}: QuestionnaireBuilderProps) {
  return (
    <div className="rounded-md border border-gray-200 bg-gray-50/60 p-3">
      <div className="mb-2 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-gray-800">Pre-questionnaire (optional)</p>
          <p className="text-[11px] text-gray-500">
            Asked when a client starts this service. OPTIONS answered on the start dialog;
            TEXT asked by the system inside the chat.
          </p>
        </div>
        <button
          type="button"
          onClick={onAddQuestion}
          className="text-xs font-medium text-brand-700 hover:underline"
        >
          + Add question
        </button>
      </div>
      {questions.length === 0 ? (
        <p className="text-[11px] text-gray-500">No questions yet.</p>
      ) : (
        <ol className="space-y-3">
          {questions.map((q, i) => (
            <li key={q.id} className="rounded-md border border-gray-200 bg-white p-3">
              <div className="grid grid-cols-[auto_1fr_140px_auto] items-center gap-2">
                <span className="text-[11px] font-semibold tabular-nums text-gray-500">
                  Q{i + 1}
                </span>
                <Input
                  value={q.label}
                  onChange={(e) => onUpdateQuestion(i, { label: e.target.value })}
                  placeholder="Question"
                />
                <select
                  value={q.type}
                  onChange={(e) =>
                    onUpdateQuestion(i, {
                      type: e.target.value as QuestionnaireAnswerType,
                      // Drop options when switching to TEXT.
                      options: e.target.value === 'OPTIONS' ? q.options : [],
                    })
                  }
                  className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                >
                  <option value="TEXT">Text answer</option>
                  <option value="OPTIONS">Pick from options</option>
                </select>
                <div className="flex items-center gap-1 text-xs">
                  <button
                    type="button"
                    onClick={() => onMoveQuestion(i, -1)}
                    disabled={i === 0}
                    className="rounded px-1 text-gray-500 hover:bg-gray-100 disabled:opacity-40"
                    aria-label="Move up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => onMoveQuestion(i, +1)}
                    disabled={i === questions.length - 1}
                    className="rounded px-1 text-gray-500 hover:bg-gray-100 disabled:opacity-40"
                    aria-label="Move down"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => onRemoveQuestion(i)}
                    className="rounded px-1 text-red-600 hover:underline"
                  >
                    Remove
                  </button>
                </div>
              </div>

              {q.type === 'OPTIONS' ? (
                <OptionsEditor
                  options={q.options}
                  onChange={(next) => onUpdateQuestion(i, { options: next })}
                />
              ) : null}

              <label className="mt-2 flex items-center gap-2 text-[11px] text-gray-600">
                <input
                  type="checkbox"
                  checked={q.required}
                  onChange={(e) => onUpdateQuestion(i, { required: e.target.checked })}
                />
                Required
              </label>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
